import { useState } from "react";
import { ArrowDown, ArrowUp, Download, FileText, MapPin, Stethoscope, Quote } from "lucide-react";

type Bi = { en: string; bn: string };
const pick = (b: Bi, isBn: boolean) => (isBn ? b.bn : b.en);

interface Props {
  isBn: boolean;
  displayFont?: string;
  bodyFont?: string;
}

type Metric = {
  label: Bi;
  before: string;
  after: string;
  delta: string;
  direction: "up" | "down";
  positive: boolean;
};

type Study = {
  id: string;
  file: string;
  fileSize: string;
  accent: string;
  accent2: string;
  badge: Bi;
  location: Bi;
  specialty: Bi;
  headline: Bi;
  summary: Bi;
  metrics: Metric[];
  quote: Bi;
  quoteBy: Bi;
  initials: string;
};

const STUDIES: Study[] = [
  {
    id: "dhanmondi-gynecology",
    file: "/case-studies/dhanmondi-gynecology.pdf",
    fileSize: "PDF · 2 pages",
    accent: "#1ba179",
    accent2: "#4de3b8",
    badge: { en: "OB/GYN · Dhanmondi", bn: "গাইনি · ধানমন্ডি" },
    location: { en: "Dhanmondi, Dhaka", bn: "ধানমন্ডি, ঢাকা" },
    specialty: { en: "Obstetrics & Gynecology", bn: "প্রসূতি ও স্ত্রীরোগ" },
    headline: {
      en: "Cut no-shows by 68% and reclaimed 4 staff hours a day",
      bn: "নো-শো ৬৮% কমেছে, প্রতিদিন ৪ ঘণ্টা স্টাফ-সময় ফিরেছে",
    },
    summary: {
      en: "A high-volume prenatal chamber turned on the WhatsApp AI agent, prenatal templates and automated reminders. Bookings became self-serve in Bengali, 24/7.",
      bn: "একটি ব্যস্ত প্রেনাটাল চেম্বার WhatsApp AI এজেন্ট, প্রেনাটাল টেমপ্লেট ও অটো-রিমাইন্ডার চালু করেছে। বুকিং ২৪/৭ বাংলায় সেলফ-সার্ভিস হয়েছে।",
    },
    metrics: [
      { label: { en: "No-show rate", bn: "নো-শো রেট" }, before: "22%", after: "7%", delta: "−68%", direction: "down", positive: true },
      { label: { en: "Booking time", bn: "বুকিং সময়" }, before: "3m 20s", after: "18s", delta: "−91%", direction: "down", positive: true },
      { label: { en: "Follow-up compliance", bn: "ফলোআপ" }, before: "44%", after: "81%", delta: "+84%", direction: "up", positive: true },
      { label: { en: "Monthly revenue", bn: "মাসিক আয়" }, before: "৳4.2L", after: "৳5.9L", delta: "+40%", direction: "up", positive: true },
    ],
    quote: {
      en: "Doctorsx replaced three tools for me. The Bengali Rx is beautiful and my patients notice.",
      bn: "Doctorsx আমার তিনটি টুল একসাথে করেছে। বাংলা Rx সত্যিই সুন্দর, রোগীরাও লক্ষ্য করেন।",
    },
    quoteBy: {
      en: "Dr. Farhana Rahman · Consultant, Gynecology",
      bn: "ডা. ফারহানা রহমান · কনসালট্যান্ট, গাইনি",
    },
    initials: "FR",
  },
  {
    id: "chattogram-medicine",
    file: "/case-studies/chattogram-medicine.pdf",
    fileSize: "PDF · 2 pages",
    accent: "#25c39a",
    accent2: "#6366f1",
    badge: { en: "Medicine · Chattogram", bn: "মেডিসিন · চট্টগ্রাম" },
    location: { en: "GEC Circle, Chattogram", bn: "জিইসি মোড়, চট্টগ্রাম" },
    specialty: { en: "Internal Medicine", bn: "ইন্টারনাল মেডিসিন" },
    headline: {
      en: "90 minutes a day back with the AI ambient scribe",
      bn: "AI অ্যাম্বিয়েন্ট স্ক্রাইবে প্রতিদিন ৯০ মিনিট ফিরেছে",
    },
    summary: {
      en: "An internist ended two hours of nightly note-writing. SOAP notes are now drafted in Bengali during the visit and signed before the patient leaves.",
      bn: "একজন মেডিসিন বিশেষজ্ঞের প্রতি রাতের দুই ঘণ্টার নোট লেখা বন্ধ হয়েছে। SOAP নোট এখন ভিজিটের মধ্যেই বাংলায় প্রস্তুত ও স্বাক্ষরিত।",
    },
    metrics: [
      { label: { en: "Notes after clinic", bn: "রাতের নোট লেখা" }, before: "118 min", after: "6 min", delta: "−95%", direction: "down", positive: true },
      { label: { en: "Patients / day", bn: "রোগী / দিন" }, before: "50", after: "62", delta: "+24%", direction: "up", positive: true },
      { label: { en: "Note completeness", bn: "নোটের পূর্ণতা" }, before: "6.4/10", after: "9.1/10", delta: "+42%", direction: "up", positive: true },
      { label: { en: "Patient NPS", bn: "রোগী NPS" }, before: "54", after: "78", delta: "+24 pts", direction: "up", positive: true },
    ],
    quote: {
      en: "I just talk to my patient — the SOAP note is ready in Bengali. Evenings are mine again.",
      bn: "শুধু রোগীর সাথে কথা বলি — SOAP নোট বাংলায় রেডি। সন্ধ্যা আবার আমার।",
    },
    quoteBy: {
      en: "Dr. Sabbir Ahmed · Medicine Specialist",
      bn: "ডা. সাব্বির আহমেদ · মেডিসিন বিশেষজ্ঞ",
    },
    initials: "SA",
  },
  {
    id: "sylhet-pediatrics",
    file: "/case-studies/sylhet-pediatrics.pdf",
    fileSize: "PDF · 2 pages",
    accent: "#059669",
    accent2: "#25c39a",
    badge: { en: "Pediatrics · Sylhet", bn: "শিশুরোগ · সিলেট" },
    location: { en: "Zindabazar, Sylhet", bn: "জিন্দাবাজার, সিলেট" },
    specialty: { en: "Pediatrics", bn: "শিশুরোগ" },
    headline: {
      en: "Recalled 318 overdue children in 90 days",
      bn: "৯০ দিনে ৩১৮ জন ওভারডিউ শিশু ফিরিয়ে আনা হয়েছে",
    },
    summary: {
      en: "Care-gap detection paired with the BD EPI vaccine schedule turned lost follow-ups into a weekly recall list of bilingual WhatsApp messages.",
      bn: "কেয়ার-গ্যাপ ডিটেকশন ও BD EPI ভ্যাকসিন শিডিউল হারানো ফলোআপকে সাপ্তাহিক দ্বিভাষিক WhatsApp রিকল লিস্টে পরিণত করেছে।",
    },
    metrics: [
      { label: { en: "Vaccine on-time", bn: "ভ্যাকসিন সময়মতো" }, before: "61%", after: "92%", delta: "+51%", direction: "up", positive: true },
      { label: { en: "Overdue recalled", bn: "ওভারডিউ রিকল" }, before: "0", after: "318", delta: "+318", direction: "up", positive: true },
      { label: { en: "Missed appointments", bn: "মিসড অ্যাপয়েন্টমেন্ট" }, before: "19%", after: "6%", delta: "−68%", direction: "down", positive: true },
      { label: { en: "New patients / mo", bn: "নতুন রোগী / মাস" }, before: "44", after: "81", delta: "+84%", direction: "up", positive: true },
    ],
    quote: {
      en: "No-show rate dropped from 22% to 7% after we turned on the WhatsApp agent. It pays for itself every week.",
      bn: "WhatsApp এজেন্ট চালু করার পর নো-শো রেট ২২% থেকে ৭%-এ নেমেছে। প্রতি সপ্তাহে খরচ উঠে আসে।",
    },
    quoteBy: {
      en: "Dr. Nusrat Jahan · Pediatrics",
      bn: "ডা. নুসরাত জাহান · শিশুরোগ",
    },
    initials: "NJ",
  },
];

const HEADING: Bi = {
  en: "Real chambers. Real numbers.",
  bn: "সত্যিকারের চেম্বার। সত্যিকারের ফলাফল।",
};
const SUB: Bi = {
  en: "Before/after outcomes from three Bangladeshi practices — 90 days after switching to Doctorsx. Download the full report for each.",
  bn: "বাংলাদেশের তিনটি চেম্বারের Doctorsx-এ যাওয়ার ৯০ দিন পরের ফলাফল। প্রতিটির পূর্ণ রিপোর্ট ডাউনলোড করুন।",
};
const KICKER: Bi = { en: "Case studies", bn: "কেস স্টাডি" };

export default function CaseStudies({ isBn, displayFont, bodyFont }: Props) {
  const dFont = displayFont ?? "'Fraunces', ui-serif, Georgia, serif";
  const bFont = bodyFont ?? "'Inter', ui-sans-serif, system-ui";
  const [active, setActive] = useState(STUDIES[0].id);
  const study = STUDIES.find((s) => s.id === active) ?? STUDIES[0];

  return (
    <section
      id="case-studies"
      className="max-w-7xl mx-auto mt-24 md:mt-40 px-4"
      style={{ fontFamily: bFont }}
    >
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide uppercase">
          <FileText className="w-3.5 h-3.5" />
          {pick(KICKER, isBn)}
        </div>
        <h2
          className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-gray-900"
          style={{ fontFamily: dFont }}
        >
          {pick(HEADING, isBn)}
        </h2>
        <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
          {pick(SUB, isBn)}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {STUDIES.map((s) => {
          const isActive = s.id === active;
          return (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                isActive
                  ? "bg-gray-900 text-white border-gray-900 shadow"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {pick(s.badge, isBn)}
            </button>
          );
        })}
      </div>

      {/* Featured study card */}
      <div
        key={study.id}
        className="relative rounded-3xl border border-gray-200 bg-white overflow-hidden shadow-sm"
      >
        {/* Top gradient band */}
        <div
          className="h-2 w-full"
          style={{
            background: `linear-gradient(90deg, ${study.accent}, ${study.accent2})`,
          }}
        />

        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-0">
          {/* LEFT: story */}
          <div className="p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-gray-100">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-4">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {pick(study.location, isBn)}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="inline-flex items-center gap-1.5">
                <Stethoscope className="w-3.5 h-3.5" />
                {pick(study.specialty, isBn)}
              </span>
            </div>

            <h3
              className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 leading-snug"
              style={{ fontFamily: dFont }}
            >
              {pick(study.headline, isBn)}
            </h3>
            <p className="mt-4 text-gray-600 leading-relaxed">
              {pick(study.summary, isBn)}
            </p>

            {/* Quote */}
            <figure
              className="mt-6 relative rounded-2xl p-5 pl-6"
              style={{
                background: `linear-gradient(135deg, ${study.accent}0d, ${study.accent2}0d)`,
                borderLeft: `3px solid ${study.accent}`,
              }}
            >
              <Quote
                className="absolute -top-3 left-4 w-6 h-6 opacity-70"
                style={{ color: study.accent }}
              />
              <blockquote className="text-[15px] text-gray-800 italic leading-relaxed">
                "{pick(study.quote, isBn)}"
              </blockquote>
              <figcaption className="mt-3 flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full text-white flex items-center justify-center text-xs font-bold"
                  style={{
                    background: `linear-gradient(135deg, ${study.accent}, ${study.accent2})`,
                  }}
                >
                  {study.initials}
                </div>
                <div className="text-xs text-gray-600">
                  {pick(study.quoteBy, isBn)}
                </div>
              </figcaption>
            </figure>

            {/* Download */}
            <a
              href={study.file}
              download
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={{
                background: `linear-gradient(135deg, ${study.accent}, ${study.accent2})`,
              }}
            >
              <Download className="w-4 h-4" />
              {isBn ? "পূর্ণ রিপোর্ট ডাউনলোড" : "Download full report"}
              <span className="text-white/70 font-normal ml-1">
                {study.fileSize}
              </span>
            </a>
          </div>

          {/* RIGHT: metrics */}
          <div className="p-8 md:p-10 bg-gradient-to-br from-gray-50 to-white">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-5">
              {isBn ? "৯০ দিনের ফলাফল" : "90-day outcomes"}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {study.metrics.map((m, i) => {
                const Arrow = m.direction === "up" ? ArrowUp : ArrowDown;
                const deltaColor = m.positive
                  ? "text-emerald-600 bg-emerald-50"
                  : "text-rose-600 bg-rose-50";
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
                      {pick(m.label, isBn)}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-gray-400 line-through">
                        {m.before}
                      </span>
                      <span
                        className="text-xl font-bold text-gray-900"
                        style={{ fontFamily: dFont }}
                      >
                        {m.after}
                      </span>
                    </div>
                    <div
                      className={`mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${deltaColor}`}
                    >
                      <Arrow className="w-3 h-3" />
                      {m.delta}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 text-[11px] text-gray-500 leading-relaxed">
              {isBn
                ? "সব তথ্য অংশগ্রহণকারী চেম্বারের সম্মতিতে বেনামে প্রকাশিত। রিপোর্টে সম্পূর্ণ পদ্ধতি ও সময়সীমা দেওয়া আছে।"
                : "All figures published anonymously with the participating chamber's consent. Full methodology and timeline in each report."}
            </div>
          </div>
        </div>
      </div>

      {/* Study index / all reports */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        {STUDIES.map((s) => (
          <a
            key={s.id}
            href={s.file}
            download
            className="group flex items-center gap-3 p-4 rounded-2xl border border-gray-200 bg-white hover:border-gray-400 hover:shadow-md transition-all"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0"
              style={{
                background: `linear-gradient(135deg, ${s.accent}, ${s.accent2})`,
              }}
            >
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {pick(s.badge, isBn)}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {s.fileSize}
              </div>
            </div>
            <Download className="w-4 h-4 text-gray-400 group-hover:text-gray-900 transition-colors" />
          </a>
        ))}
      </div>
    </section>
  );
}
